import { CustomDataGrid, StackedTextCell } from '@retail/components';
import { useGroupedRowClassName } from '@retail/hooks';
import { PricePerUnit, useMgItemAndExternalNumberColumn } from '@retail/products/components';
import { Chip, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { PriceControlItem } from '@retail/price-control/types';
import {
  GridCallbackDetails,
  GridColDef,
  GridRowId,
  GridRowSelectionModel,
  useGridApiRef,
} from '@mui/x-data-grid-premium';
import { useMemo } from 'react';
import { East, ReadMore } from '@mui/icons-material';
import { AppTenant } from '@retail/auth/types';
import { useMyAssortmentTFunction } from '@retail/my-assortment/i18n';
import { PriceChangeTriggerChip } from './PriceChangeTriggerChip';
import { getLLDate } from '@retail/utils';
import { getActionIcon } from './ItemAction';
import { getPriceSuggestionType } from '@retail/products/utils';
import { usePriceSuggestionTFunction } from '@retail/price-suggestion/i18n';

const noValue = '-';

const baseColumnProps: Partial<GridColDef> = {
  width: 140,
  headerName: '',
  headerAlign: 'left',
  align: 'left',
};

interface Props {
  supplierItems: PriceControlItem[];
  appTenant: AppTenant;
  isLoadingFetchItems: boolean;
  selectedItemId?: number;
  checkedItems: number[];
  includeVat: boolean;
  setItemToHandle: (item?: PriceControlItem) => void;
  setCheckedItems: (items: number[]) => void;
}
const itemIdSelector = (item: PriceControlItem) => item.memberEventId;

export function PriceControlSupplierItemsTable({
  supplierItems,
  appTenant,
  isLoadingFetchItems,
  selectedItemId,
  checkedItems,
  includeVat,
  setItemToHandle,
  setCheckedItems,
}: Props) {
  const t = useMyAssortmentTFunction();
  const priceSuggestionT = usePriceSuggestionTFunction();

  const onRowsSelectionHandler = (
    selectionModel: GridRowSelectionModel,
    details: GridCallbackDetails
  ) => {
    if (details.reason === 'singleRowSelection') {
      setCheckedItems(Array.from(selectionModel.ids) as number[]);
    } else if (checkedItems.length !== supplierItems.length) {
      setCheckedItems(supplierItems.map(itemIdSelector));
    } else {
      setCheckedItems([]);
    }
  };

  const selectionModel: GridRowSelectionModel = {
    type: 'include',
    ids: new Set<GridRowId>(checkedItems),
  };

  const apiRef = useGridApiRef();
  const getRowClassName = useGroupedRowClassName<PriceControlItem>({
    apiRef,
    getGroupId: (row) => row.mgItemNumber,
    getSelectionId: (row) => row.mgItemNumber,
    selectedId: selectedItemId,
  });

  const mgItemAndExternalNumberColumn = useMgItemAndExternalNumberColumn<PriceControlItem>({
    externalItemSource: appTenant.externalItemSource,
    columnProps: baseColumnProps,
  });

  const columns: GridColDef<PriceControlItem>[] = useMemo(
    () => [
      {
        ...baseColumnProps,
        minWidth: 50,
        width: 50,
        field: 'isHandled',
        headerName: '',
        renderCell: ({ row }) => {
          if (row.handledAction) {
            const eventAction = getActionIcon(row.handledAction, t);
            return (
              <Tooltip
                title={
                  <Stack>
                    <Typography variant="subtitle2">{eventAction.title}</Typography>
                    <Typography variant="body2">
                      {t('myAssortment.priceControl.handled')}: {getLLDate(row.handledAtDate)}
                    </Typography>
                  </Stack>
                }
              >
                <Stack flexDirection="column" alignItems="center">
                  {eventAction.icon}
                  <Typography fontSize={9}>{t('myAssortment.priceControl.handled')}</Typography>
                </Stack>
              </Tooltip>
            );
          } else {
            return '';
          }
        },
      },
      mgItemAndExternalNumberColumn,
      {
        ...baseColumnProps,
        field: 'itemText',
        width: 260,
        headerName: t('myAssortment.columns.itemText'),
        renderCell: ({ row }) => (
          <StackedTextCell topLine={row.primaryText} bottomLine={row.secondaryText} />
        ),
        valueGetter: (_, row) => row.primaryText,
      },
      {
        ...baseColumnProps,
        headerName: t('myAssortment.columns.discountGroup'),
        field: 'discountGroup',
        renderCell: ({ row }) => (
          <StackedTextCell
            topLine={row.discountGroup?.name}
            bottomLine={row.discountGroup?.code}
            nullableBottomLine
          />
        ),
      },
      {
        ...baseColumnProps,
        headerName: t('myAssortment.levels.productGroup'),
        field: 'productGroup',
        renderCell: ({ row }) => (
          <StackedTextCell
            topLine={row.productGroup?.name}
            bottomLine={row.productGroup?.groupNumber}
            nullableBottomLine
          />
        ),
      },
      {
        ...baseColumnProps,
        field: 'changeTrigger',
        headerName: t('myAssortment.priceControl.trigger.changeTrigger'),
        width: 210,
        renderCell: ({ row }) => <PriceChangeTriggerChip trigger={row.eventType} />,
      },
      {
        ...baseColumnProps,
        field: 'changeDate',
        headerName: t('myAssortment.priceControl.changeDate'),
        width: 140,
        renderCell: ({ row }) => getLLDate(row.changeDate),
      },
      {
        ...baseColumnProps,
        field: 'purchasePriceChange',
        renderHeader: () => (
          <StackedTextCell
            topLine={t('myAssortment.columns.purchasePrice.price')}
            bottomLine={
              <Stack flexDirection="row" alignItems="center" gap={0.5}>
                {t('myAssortment.priceControl.old')}
                <East fontSize="small" />
                {t('myAssortment.priceControl.new')}
              </Stack>
            }
          />
        ),
        width: 210,
        renderCell: ({ row }) => {
          const priceAfter = row.pricesExVat?.purchasePriceAfter;
          const priceBefore = row.pricesExVat?.purchasePriceBefore;
          const percentChange = row.purchasePriceDiffPercentage;

          if (!priceAfter && !priceBefore && !percentChange) {
            return noValue;
          }

          const percentChangeLabel = !percentChange
            ? noValue
            : percentChange > 0
            ? `+ ${percentChange} %`
            : `${percentChange} %`;

          return (
            <Stack>
              <StackedTextCell
                topLine={
                  !priceAfter ? noValue : <PricePerUnit price={priceAfter} unit={row.primaryUnit} />
                }
                bottomLine={
                  !priceBefore ? (
                    noValue
                  ) : (
                    <PricePerUnit price={priceBefore} unit={row.primaryUnit} />
                  )
                }
                gap="small"
                slotProps={{ bottomLine: { lineThrough: !!priceBefore } }}
              />
              <Typography
                color={!percentChange ? 'textDisabled' : percentChange > 0 ? 'success' : 'error'}
                variant="body3"
              >
                {percentChangeLabel}
              </Typography>
            </Stack>
          );
        },
      },
      {
        ...baseColumnProps,
        field: 'salesPriceChange',
        renderHeader: () => (
          <StackedTextCell
            topLine={t('myAssortment.columns.salesPrice')}
            bottomLine={
              <Stack flexDirection="row" alignItems="center" gap={0.5}>
                {t('myAssortment.priceControl.old')}
                <East fontSize="small" />
                {t('myAssortment.priceControl.new')}
              </Stack>
            }
          />
        ),
        width: 210,
        renderCell: ({ row }) => {
          const percentChange = row.salesPriceDiffPercentage;
          const prices = includeVat ? row.pricesIncVat : row.pricesExVat;
          const priceAfter = prices?.salesPriceAfter;
          const priceBefore = prices?.salesPriceBefore;

          if (!priceAfter && !priceBefore && !percentChange) {
            return noValue;
          }

          const percentChangeLabel = !percentChange
            ? noValue
            : percentChange > 0
            ? `+ ${percentChange.toFixed(2)} %`
            : `${percentChange.toFixed(2)} %`;

          return (
            <Stack>
              <StackedTextCell
                topLine={
                  !priceAfter ? noValue : <PricePerUnit price={priceAfter} unit={row.primaryUnit} />
                }
                bottomLine={
                  !priceBefore ? (
                    noValue
                  ) : (
                    <PricePerUnit price={priceBefore} unit={row.primaryUnit} />
                  )
                }
                nullableBottomLine
                gap="small"
                slotProps={{ bottomLine: { lineThrough: !!priceBefore } }}
              />
              <Typography
                color={!percentChange ? 'textDisabled' : percentChange > 0 ? 'success' : 'error'}
                variant="body3"
              >
                {percentChangeLabel}
              </Typography>
            </Stack>
          );
        },
      },
      {
        ...baseColumnProps,
        field: 'salesCoverageChange',
        renderHeader: () => (
          <StackedTextCell
            topLine={t('myAssortment.columns.salesCoverage')}
            bottomLine={
              <Stack flexDirection="row" alignItems="center" gap={0.5}>
                {t('myAssortment.priceControl.old')}
                <East fontSize="small" />
                {t('myAssortment.priceControl.new')}
              </Stack>
            }
          />
        ),
        width: 210,
        renderCell: ({ row }) => {
          const coverageAfter = row.coverageDegreeAfter;
          const coverageBefore = row.coverageDegreeBefore;

          if (!coverageAfter && !coverageBefore) {
            return noValue;
          }

          return (
            <StackedTextCell
              topLine={!coverageAfter ? noValue : `${coverageAfter} %`}
              bottomLine={!coverageBefore ? noValue : `${coverageBefore} %`}
              nullableBottomLine
              gap="small"
              slotProps={{ bottomLine: { lineThrough: !!coverageBefore } }}
            />
          );
        },
      },
      {
        ...baseColumnProps,
        field: 'salesFactorChange',
        renderHeader: () => (
          <StackedTextCell
            topLine={t('myAssortment.columns.freightCondition.factor')}
            bottomLine={
              <Stack flexDirection="row" alignItems="center" gap={0.5}>
                {t('myAssortment.priceControl.old')}
                <East fontSize="small" />
                {t('myAssortment.priceControl.new')}
              </Stack>
            }
          />
        ),
        width: 210,
        renderCell: ({ row }) => {
          const dgChange = row.salesFactorDiffPercentage;
          const factorAfter = row.salesFactorAfter;
          const factorBefore = row.salesFactorBefore;

          if (!factorAfter && !factorBefore && !dgChange) {
            return noValue;
          }

          return (
            <Stack>
              <StackedTextCell
                topLine={factorAfter || noValue}
                bottomLine={factorBefore || noValue}
                nullableBottomLine
                gap="small"
                slotProps={{ bottomLine: { lineThrough: !!factorBefore } }}
              />
              <Typography
                color={!dgChange ? 'textDisabled' : dgChange > 0 ? 'success' : 'error'}
                variant="body3"
              >
                {!dgChange ? noValue : `${dgChange} %`}
              </Typography>
            </Stack>
          );
        },
      },
      {
        ...baseColumnProps,
        field: 'oldPriceSource',
        headerName: t('myAssortment.columns.currentPriceSource'),
        renderCell: ({ row }) => {
          const priceSource = getPriceSuggestionType(priceSuggestionT, row.priceSourceBefore);

          if (!priceSource) {
            return noValue;
          }

          return (
            <Chip
              variant="outlined"
              size="small"
              label={priceSource}
              sx={{
                height: 'auto',
                '& .MuiChip-label': {
                  display: '-webkit-box',
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: 2,
                  whiteSpace: 'normal',
                  overflow: 'hidden',
                  py: 0.25,
                },
              }}
            />
          );
        },
      },
      {
        ...baseColumnProps,
        width: 50,
        minWidth: 50,
        field: 'actions',
        renderCell: ({ row }) => (
          <Tooltip title={t('myAssortment.actions.handleItem')}>
            <IconButton
              onClick={() =>
                selectedItemId === row.mgItemNumber
                  ? setItemToHandle(undefined)
                  : setItemToHandle(row)
              }
              sx={{ p: 0 }}
            >
              <ReadMore fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    [
      includeVat,
      mgItemAndExternalNumberColumn,
      priceSuggestionT,
      selectedItemId,
      setItemToHandle,
      t,
    ]
  );
  return (
    <CustomDataGrid
      apiRef={apiRef}
      disableRowSelectionOnClick
      getRowClassName={getRowClassName}
      getRowId={(row) => row.memberEventId}
      rowHeight={70}
      disableColumnMenu
      loading={isLoadingFetchItems}
      hideFooter
      checkboxSelection
      rowSelectionModel={selectionModel}
      onRowSelectionModelChange={onRowsSelectionHandler}
      rows={supplierItems}
      columns={columns}
      initialState={{
        pinnedColumns: { right: ['actions'] },
      }}
    />
  );
}
